/**
 * "Home Free" custom looks (ENG-2291), from the SET.Live Screen Takeover comps
 * (Figma page "Home Free", node 354:5551, artwork "Frame 1" 490:17974).
 *
 * One large Memphis-style artwork: a gridded background, black dots and color
 * blocks with black shadows. Each phone shows one of the comps' eight
 * phone-sized crops of it, so together the crowd shows the whole picture.
 * The blocks and dots float gently over a still background. The multicolor
 * version gives each crop the background color from its comp.
 *
 * Coordinates are artwork units, with the artwork's top-left at 0,0.
 */

type Ctx = CanvasRenderingContext2D;

/** A phone screen in the comps; each crop is this size. */
const SCREEN = { width: 375, height: 812 };

const TEAL = { background: "#43c5cd", grid: "#69d1d7" };
const SALMON = { background: "#edaba1", grid: "#f2beb6" };
const PURPLE = { background: "#c29ada", grid: "#d2b4e4" };
const YELLOW = { background: "#efea98", grid: "#f9f6c3" };

/** The Home Free colors, shared by its other looks (./homeFreeTwinkle). */
export const HOME_FREE_PALETTE = {
  teal: TEAL.background,
  purple: PURPLE.background,
  salmon: SALMON.background,
  yellow: YELLOW.background,
};

/**
 * The comps' crops (where each phone screen sits on the artwork), with the
 * colors each one has in the multicolor version.
 */
const CROPS = [
  { x: 983.5, y: 115, multicolor: TEAL },
  { x: 350.5, y: 999.5, multicolor: TEAL },
  { x: 15.5, y: 789.5, multicolor: SALMON },
  { x: 39.5, y: 115, multicolor: TEAL },
  { x: 711.5, y: 115, multicolor: PURPLE },
  { x: 675.5, y: 845.5, multicolor: PURPLE },
  { x: 1404.5, y: 521, multicolor: TEAL },
  { x: 1011.5, y: 927, multicolor: YELLOW },
];

/**
 * The comps' grid is evenly spaced: 30-wide strokes every GRID_PITCH, each
 * ending at a line (above / left of it) offset from the artwork's top-left.
 * It repeats, so it reaches every edge of any screen.
 */
const GRID_STROKE = 30;
const GRID_PITCH = 243.64;
const GRID_ROW_OFFSET = 81;
const GRID_COLUMN_OFFSET = 46.3;

/** Black dots: [center x, center y, radius]. */
const DOTS: [number, number, number][] = [
  [701, 1772.6, 40],
  [76.1, 1431.9, 40],
  [1029.5, 583.9, 40],
  [340.5, 1158.5, 40],
  [650.2, 115.9, 34.9],
  [1063.1, 277.4, 25.9],
  [1479.8, 65.6, 25.9],
  [835.2, 70.1, 21.4],
  [1751.7, 133.4, 21.4],
  [390.2, 1373.1, 28.3],
  [76.1, 490.6, 28.3],
  [1254.2, 543.9, 28.3],
  [112.6, 815.1, 24.8],
  [1290.7, 868.4, 24.8],
  [563.9, 268.9, 18],
  [1449.1, 1828, 15.3],
  [125.3, 1657.4, 28.3],
  [872.7, 1757.2, 19.7],
  [1148.5, 1451.9, 17.7],
  [79.3, 1061.7, 17.7],
  [1734.1, 1001.4, 17.7],
  [833.7, 1146.3, 17.7],
  [1393, 347, 17.7],
  [714.6, 707.6, 17.7],
  [1598.4, 354.2, 10.5],
  [920, 714.9, 10.5],
  [877.4, 375.1, 10.5],
  [771.1, 260.3, 13.5],
  [563.5, 1633.6, 17.7],
  [246.5, 996.1, 17.7],
  [1326.5, 1081.4, 17.7],
  [387.4, 1828, 17.7],
  [1362.5, 1401.4, 17.7],
  [1240.1, 1828, 17.7],
  [685.8, 1528.8, 28.3],
  [368.8, 891.2, 28.3],
  [1448.8, 976.6, 28.3],
  [1767.5, 1242.3, 22.9],
  [867.1, 1387.2, 22.9],
  [1739.3, 627, 22.9],
  [1264.5, 1573.3, 12.1],
  [1464.4, 1684, 12.1],
  [1581.2, 1446.3, 12.1],
  [1069.5, 1201.6, 12.1],
  [1706.6, 1603.9, 12.1],
  [1057.5, 1679.9, 12.1],
  [216.8, 1603.9, 12.1],
  [137.3, 1286.9, 12.1],
  [557.9, 1422.2, 12.1],
  [180.9, 677.9, 12.1],
  [1359, 731.3, 12.1],
  [205, 389.2, 12.1],
  [75.7, 91.5, 12.1],
  [601.6, 971, 34.9],
  [424, 564.9, 25.9],
  [770.9, 923.9, 18],
];

/**
 * One layer of a block: a w × h box centered at cx, cy, turned `rotate`
 * degrees (and mirrored vertically with flipY), holding an SVG path in box
 * coordinates, or filled whole when there's no path.
 */
interface Layer {
  fill: string;
  cx: number;
  cy: number;
  w: number;
  h: number;
  rotate?: number;
  flipY?: boolean;
  d?: string;
}

const SHADOW = "#231f20";

/** Color blocks, each a black shadow under a colored shape, in comp order. */
const BLOCKS: Layer[][] = [
  // Yellow half circle.
  [
    {
      fill: SHADOW,
      cx: 1768.5,
      cy: 1158.7,
      w: 620.4,
      h: 620.4,
      d: "M256.233 564.684C377.366 564.684 475.563 466.487 475.563 345.354C475.563 224.221 377.366 126.024 256.233 126.024C135.1 126.024 36.9023 224.221 36.9023 345.354C36.9023 466.487 135.1 564.684 256.233 564.684Z",
    },
    {
      fill: "#efea98",
      cx: 1768.5,
      cy: 1158.7,
      w: 620.4,
      h: 620.4,
      d: "M465.27 465.27C550.924 379.616 550.924 240.744 465.27 155.09C379.616 69.4362 240.744 69.4362 155.09 155.09C69.4362 240.744 69.4362 379.616 155.09 465.27C240.744 550.924 379.616 550.924 465.27 465.27Z",
    },
  ],
  // Purple half circle.
  [
    {
      fill: SHADOW,
      cx: 259.8,
      cy: 836.4,
      w: 447.6,
      h: 447.6,
      d: "M184.857 407.388C272.248 407.388 343.092 336.544 343.092 249.153C343.092 161.763 272.248 90.9189 184.857 90.9189C97.467 90.9189 26.623 161.763 26.623 249.153C26.623 336.544 97.467 407.388 184.857 407.388Z",
    },
    {
      fill: "#c29ada",
      cx: 259.8,
      cy: 836.4,
      w: 447.6,
      h: 447.6,
      d: "M335.666 335.666C397.46 273.872 397.46 173.683 335.666 111.889C273.872 50.0943 173.683 50.0943 111.889 111.889C50.0943 173.683 50.0943 273.872 111.889 335.666C173.683 397.46 273.872 397.46 335.666 335.666Z",
    },
  ],
  // Purple triangle.
  [
    {
      fill: SHADOW,
      cx: 1172.8,
      cy: 1797.1,
      w: 835.1,
      h: 798.3,
      d: "M74.3789 590.977L835.069 798.263L684.154 91.187L74.3789 590.977Z",
    },
    {
      fill: "#c29ada",
      cx: 1172.8,
      cy: 1797.1,
      w: 835.1,
      h: 798.3,
      d: "M0 499.79L760.69 707.076L609.775 0L0 499.79Z",
    },
  ],
  // Salmon triangle.
  [
    {
      fill: SHADOW,
      cx: 271.9,
      cy: 172.3,
      w: 602.2,
      h: 575.6,
      d: "M53.6347 426.156L602.171 575.631L493.346 65.7553L53.6347 426.156Z",
    },
    {
      fill: "#edaba1",
      cx: 271.9,
      cy: 172.3,
      w: 602.2,
      h: 575.6,
      d: "M0 360.4L548.537 509.875L439.711 0L0 360.4Z",
    },
  ],
  // Purple zigzag.
  [
    {
      fill: "#000000",
      cx: 1855.6,
      cy: 721.9,
      w: 1152.8,
      h: 896.1,
      rotate: 51.59,
      d: "M150.191 519.512L23.1015 874.107L92.1376 896.073L194.123 597.962L427.904 685.826L547.148 353.198L882.915 423.803L1152.78 77.0534L1096.3 36.2593L860.949 353.198L503.216 266.903L398.093 597.962L150.191 519.512Z",
    },
    {
      fill: "#c29ada",
      cx: 1855.6,
      cy: 721.9,
      w: 1152.8,
      h: 896.1,
      rotate: 51.59,
      d: "M128.576 488.907L0 847.651L69.8439 869.874L173.022 568.275L409.539 657.167L530.179 320.647L869.874 392.078L1142.9 41.2714L1085.75 0L847.651 320.647L485.732 233.342L379.379 568.275L128.576 488.907Z",
    },
  ],
  // Yellow zigzag.
  [
    {
      fill: "#000000",
      cx: -123.3,
      cy: 1474.1,
      w: 1269.8,
      h: 987,
      rotate: 157.81,
      flipY: true,
      d: "M165.431 572.23L25.4457 962.807L101.487 987.002L213.822 658.641L471.326 755.421L602.671 389.039L972.51 466.809L1269.76 84.8723L1207.55 39.9386L948.314 389.039L554.281 293.987L438.49 658.641L165.431 572.23Z",
    },
    {
      fill: "#efea98",
      cx: -123.3,
      cy: 1474.1,
      w: 1269.8,
      h: 987,
      rotate: 157.81,
      flipY: true,
      d: "M141.624 538.519L0 933.667L76.9313 958.145L190.58 625.941L451.097 723.854L583.979 353.185L958.145 431.865L1258.88 45.4594L1195.93 0L933.667 353.185L535.023 257.021L417.877 625.941L141.624 538.519Z",
    },
  ],
  // Salmon square.
  [
    { fill: "#000000", cx: 867.2, cy: 1100.8, w: 526, h: 526, rotate: -14.14 },
    { fill: "#edaba1", cx: 807.7, cy: 1065.2, w: 526, h: 526, rotate: -14.14 },
  ],
  // Yellow square.
  [
    {
      fill: "#000000",
      cx: 1190.5,
      cy: 156.8,
      w: 396.6,
      h: 396.6,
      rotate: 15.49,
    },
    {
      fill: "#efea98",
      cx: 1164.7,
      cy: 111.3,
      w: 396.6,
      h: 396.6,
      rotate: 15.49,
    },
  ],
];

// --- floating ----------------------------------------------------------------

/** How far blocks and dots drift from their spot, in artwork units. */
const BLOCK_DRIFT = 18;
const DOT_DRIFT = 10;
/** How far blocks turn either way, in degrees. */
const BLOCK_TURN = 2;
/** Each element drifts on its own rhythm, between these periods (seconds). */
const MIN_PERIOD = 5;
const MAX_PERIOD = 9;

/** A fixed pseudo-random number in [0, 1) for an element and a property. */
const hash = (index: number, salt: number) => {
  const v = Math.sin(index * 127.1 + salt * 311.7) * 43758.5453;
  return v - Math.floor(v);
};

/**
 * A slow wave in [-1, 1] for an element's property. Every phone computes the
 * same value at the same moment, so phones sharing a crop float together.
 */
const wave = (t: number, index: number, salt: number) => {
  const period = MIN_PERIOD + hash(index, salt) * (MAX_PERIOD - MIN_PERIOD);
  const phase = hash(index, salt + 7) * 2 * Math.PI;
  return Math.sin((2 * Math.PI * t) / period + phase);
};

// --- drawing -----------------------------------------------------------------

const paths = new Map<string, Path2D>();
const pathFor = (d: string) => {
  let path = paths.get(d);
  if (!path) {
    path = new Path2D(d);
    paths.set(d, path);
  }
  return path;
};

const drawLayer = (ctx: Ctx, layer: Layer) => {
  ctx.save();
  ctx.translate(layer.cx, layer.cy);
  if (layer.rotate) ctx.rotate((layer.rotate * Math.PI) / 180);
  if (layer.flipY) ctx.scale(1, -1);
  ctx.translate(-layer.w / 2, -layer.h / 2);
  ctx.fillStyle = layer.fill;
  if (layer.d) ctx.fill(pathFor(layer.d));
  else ctx.fillRect(0, 0, layer.w, layer.h);
  ctx.restore();
};

/**
 * Where a comp crop lands on the canvas. Positions stretch to the canvas's
 * width and height separately, so the whole comp fills any screen. Shapes keep
 * their proportions at one size (`size`, between the two stretches), so
 * circles stay round and shadows stay with their shapes.
 */
interface Frame {
  crop: (typeof CROPS)[number];
  sx: number;
  sy: number;
  size: number;
}

const toX = (frame: Frame, x: number) => (x - frame.crop.x) * frame.sx;
const toY = (frame: Frame, y: number) => (y - frame.crop.y) * frame.sy;

const drawBlock = (
  ctx: Ctx,
  frame: Frame,
  layers: Layer[],
  t: number,
  index: number,
) => {
  // Float around the block's middle, shadow and shape together.
  const cx = layers.reduce((sum, l) => sum + l.cx, 0) / layers.length;
  const cy = layers.reduce((sum, l) => sum + l.cy, 0) / layers.length;
  ctx.save();
  ctx.translate(
    toX(frame, cx) + BLOCK_DRIFT * frame.size * wave(t, index, 1),
    toY(frame, cy) + BLOCK_DRIFT * frame.size * wave(t, index, 2),
  );
  ctx.rotate((BLOCK_TURN * wave(t, index, 3) * Math.PI) / 180);
  ctx.scale(frame.size, frame.size);
  ctx.translate(-cx, -cy);
  layers.forEach((layer) => drawLayer(ctx, layer));
  ctx.restore();
};

const drawDots = (ctx: Ctx, frame: Frame, t: number) => {
  ctx.fillStyle = "#000000";
  ctx.beginPath();
  DOTS.forEach(([x, y, r], i) => {
    const index = 100 + i;
    const cx = toX(frame, x) + DOT_DRIFT * frame.size * wave(t, index, 1);
    const cy = toY(frame, y) + DOT_DRIFT * frame.size * wave(t, index, 2);
    const radius = r * frame.size;
    ctx.moveTo(cx + radius, cy);
    ctx.arc(cx, cy, radius, 0, 2 * Math.PI);
  });
  ctx.fill();
};

/** The middles of the grid strokes around `from` to `to`, in artwork units. */
const gridLines = (offset: number, from: number, to: number) => {
  const lines = [];
  const middle = offset - GRID_STROKE / 2;
  const first =
    middle + Math.floor((from - GRID_PITCH - middle) / GRID_PITCH) * GRID_PITCH;
  for (let line = first; line < to + GRID_PITCH; line += GRID_PITCH) {
    lines.push(line);
  }
  return lines;
};

/** Draws the grid edge to edge, strokes at the shapes' size. */
const drawGrid = (
  ctx: Ctx,
  frame: Frame,
  color: string,
  w: number,
  h: number,
) => {
  const stroke = GRID_STROKE * frame.size;
  const { x, y } = frame.crop;
  ctx.fillStyle = color;
  gridLines(GRID_ROW_OFFSET, y, y + SCREEN.height).forEach((line) =>
    ctx.fillRect(0, toY(frame, line) - stroke / 2, w, stroke),
  );
  gridLines(GRID_COLUMN_OFFSET, x, x + SCREEN.width).forEach((line) =>
    ctx.fillRect(toX(frame, line) - stroke / 2, 0, stroke, h),
  );
};

// The comps' background has a fine white speckle (a Figma noise effect). It's
// drawn from a small tile, built once, at the shapes' size so it scales with
// the art and stays fine on thumbnails.
const SPECKLE_TILE = 256;
const SPECKLES = 420;
let speckleTile: HTMLCanvasElement | null | undefined;
const speckles = new WeakMap<Ctx, CanvasPattern | null>();

const getSpeckleTile = () => {
  if (speckleTile !== undefined) return speckleTile;
  speckleTile = null;
  if (typeof document === "undefined") return speckleTile;
  const canvas = document.createElement("canvas");
  canvas.width = SPECKLE_TILE;
  canvas.height = SPECKLE_TILE;
  const tileCtx = canvas.getContext("2d");
  if (!tileCtx) return speckleTile;
  // Seeded, so every phone gets the same texture.
  let seed = 1;
  const next = () => (seed = (seed * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < SPECKLES; i++) {
    tileCtx.fillStyle = `rgba(255, 255, 255, ${0.3 + next() * 0.5})`;
    const size = next() < 0.8 ? 1 : 1.5;
    tileCtx.fillRect(next() * SPECKLE_TILE, next() * SPECKLE_TILE, size, size);
  }
  speckleTile = canvas;
  return speckleTile;
};

const drawSpeckles = (ctx: Ctx, frame: Frame, w: number, h: number) => {
  if (!speckles.has(ctx)) {
    const tile = getSpeckleTile();
    speckles.set(ctx, tile ? ctx.createPattern(tile, "repeat") : null);
  }
  const pattern = speckles.get(ctx);
  if (!pattern) return;
  ctx.save();
  ctx.scale(frame.size, frame.size);
  ctx.fillStyle = pattern;
  ctx.fillRect(0, 0, w / frame.size, h / frame.size);
  ctx.restore();
};

/**
 * Draws a phone's crop of the artwork across the whole canvas (see
 * {@link Frame}). `seed` picks the crop, so a phone keeps the same one for the
 * whole show.
 */
export const renderHomeFree = (
  ctx: Ctx,
  w: number,
  h: number,
  t: number,
  seed: number,
  multicolor: boolean,
) => {
  if (!w || !h) return;
  const crop = CROPS[Math.floor(seed * CROPS.length) % CROPS.length];
  const colors = multicolor ? crop.multicolor : TEAL;
  const sx = w / SCREEN.width;
  const sy = h / SCREEN.height;
  const frame: Frame = { crop, sx, sy, size: Math.sqrt(sx * sy) };

  ctx.fillStyle = colors.background;
  ctx.fillRect(0, 0, w, h);
  drawSpeckles(ctx, frame, w, h);
  drawGrid(ctx, frame, colors.grid, w, h);
  drawDots(ctx, frame, t);
  BLOCKS.forEach((layers, i) => drawBlock(ctx, frame, layers, t, i));
};

/** The artwork's colors, for swatches. */
export const HOME_FREE_COLORS = [
  TEAL.background,
  YELLOW.background,
  PURPLE.background,
  SALMON.background,
];
